<script setup lang="ts">
import Sidebarv2Header from '~/components/sidebarv2/Header.vue';
import Sidebarv2MenuItem from '~/components/sidebarv2/MenuItem.vue';
import Sidebarv2CasinoItems from '~/components/sidebarv2/menu/CasinoItems.vue';
import Sidebarv2SportsItems from '~/components/sidebarv2/menu/SportsItems.vue';

const emit =
  defineEmits<(e: 'menu-effect-end', payload: 'open' | 'close') => void>();

let menuEffectTimer: ReturnType<typeof setTimeout> | null = null;

const { openSupportChat, isLoadingOpenSupportChat } = useLiveSupport();

const { isCactusSportsbookActive, openBettingRulesInSportsbook } =
  useSportsBook();

const route = useRoute();

const layoutStore = useLayoutStore();
const { closeMenu, toggleMenuOpen } = layoutStore;
const isDarkTheme = computed(() => layoutStore.isDarkTheme);
const { isSidebarMenuOpen, isLayoutMobileOrTablet } = useLayout();

watch(
  () => isSidebarMenuOpen.value,
  (val) => {
    if (menuEffectTimer) clearTimeout(menuEffectTimer);
    menuEffectTimer = setTimeout(() => {
      emit('menu-effect-end', val ? 'open' : 'close');
      menuEffectTimer = null;
    }, 300);
  },
);

onUnmounted(() => {
  if (menuEffectTimer) clearTimeout(menuEffectTimer);
});

const { isAuthenticated } = storeToRefs(useAuthStore());
const { casinoNomenclature } = useCasino();
const { openRegister } = useAuth();
const { isHomeAsSportbookEnabled } = useFeatureFlagsNew();

const casinoRoute = computed(() =>
  isHomeAsSportbookEnabled.value ? '/games' : '/',
);

const sportsRoute = computed(() =>
  isHomeAsSportbookEnabled.value ? '/' : '/sports',
);

const { linkToHelpCenter, finalLinkToBlog, linkToBlogTargetBlank } = useLinks();

function onMenuToggle() {
  toggleMenuOpen();
}

function onMenuItemClick() {
  if (isLayoutMobileOrTablet.value) {
    closeMenu();
  }
}

const activeTab = ref<'casino' | 'sports'>('casino');

const getTabFromRoute = () => {
  if (route.path.startsWith('/sports')) {
    return 'sports';
  }
  if (isHomeAsSportbookEnabled.value && route.path === '/') {
    return 'sports';
  }

  return 'casino';
};

onMounted(() => {
  activeTab.value = getTabFromRoute();
});

watch(
  () => route.fullPath,
  () => {
    activeTab.value = getTabFromRoute();
  },
);

const socialLinks = [
  {
    icon: 'menu-lateral/instagram',
    url: 'https://instagram.com/apostatudo.bet.br',
    label: 'Instagram',
  },
  {
    icon: 'menu-lateral/telegram',
    url: 'https://t.me/+ulZC4cxSwclkMjhh',
    label: 'Telegram',
  },
  {
    icon: 'menu-lateral/youtube',
    url: 'https://youtube.com/@apostatudo.betofc',
    label: 'YouTube',
  },
];
</script>

<template>
  <Sidebarv2Header
    :is-sidebar-closed="!isSidebarMenuOpen"
    @close-menu="closeMenu()"
    @on-click-menu="onMenuToggle()"
  />

  <div
    :class="[
      $style.sidebarContent,
      {
        [$style.isClosed]: !isSidebarMenuOpen,
        [$style.darkTheme]: isDarkTheme,
      },
    ]"
    :draggable="false"
  >
    <!-- Abas Cassino/Esporte -->
    <div
      v-if="isSidebarMenuOpen"
      :class="[$style.tabsContainer]"
    >
      <div :class="[$style.tabsWrapper, { [$style.darkTheme]: isDarkTheme }]">
        <button
          v-tooltip.desktopOnly="casinoNomenclature"
          :class="[$style.tab, activeTab === 'casino' ? $style.active : '']"
          @click="navigateTo(casinoRoute)"
        >
          <span>{{ casinoNomenclature }}</span>
        </button>

        <button
          v-tooltip.desktopOnly="$t('sidebar.sports')"
          :class="[$style.tab, activeTab === 'sports' ? $style.active : '']"
          @click="navigateTo(sportsRoute)"
        >
          <span>{{ $t('sidebar.sports') }}</span>
        </button>
      </div>
    </div>

    <!-- Conteúdo baseado na aba ativa -->
    <Sidebarv2CasinoItems
      v-if="activeTab === 'casino'"
      :is-logged-user="isAuthenticated"
      @on-click-menu="onMenuItemClick()"
      @open-register-modal="openRegister"
    />
    <Sidebarv2SportsItems
      v-else-if="activeTab === 'sports'"
      :is-logged-user="isAuthenticated"
      @on-click-menu="onMenuItemClick()"
      @open-register-modal="openRegister"
    />

    <!-- Precisa de Ajuda? -->
    <div
      v-if="isSidebarMenuOpen"
      :class="$style.helpSection"
    >
      <span :class="$style.sectionTitle">Precisa de Ajuda?</span>

      <div :class="$style.helpContainer">
        <Sidebarv2MenuItem
          glow-color="#0606ad"
          is-button
          @click="openSupportChat()"
        >
          <BaseLoading
            v-if="isLoadingOpenSupportChat"
            class="sidebarIcon !bg-transparent !relative"
            :show="true"
            :size="'xsmall'"
          />
          <NuxtIcon
            v-else
            class="sidebarIcon"
            name="menu-lateral/suporte"
          />
          <span>{{ $t('sidebar.live_support') }}</span>
        </Sidebarv2MenuItem>

        <Sidebarv2MenuItem
          :draggable="false"
          glow-color="#0606ad"
          :is-active="route.path === '/blog'"
          to="/blog"
        >
          <NuxtIcon
            class="sidebarIcon"
            name="menu-lateral/blog"
          />
          <span>{{ $t('sidebar.blog') }}</span>
        </Sidebarv2MenuItem>

        <Sidebarv2MenuItem
          :draggable="false"
          glow-color="#0606ad"
          target="_blank"
          to="https://atendimento.apostatudo.bet.br/"
        >
          <NuxtIcon
            class="sidebarIcon"
            name="menu-lateral/central-ajuda"
          />
          <span>{{ $t('sidebar.help_center') }}</span>
        </Sidebarv2MenuItem>

        <Sidebarv2MenuItem
          v-if="isCactusSportsbookActive"
          glow-color="#0606ad"
          is-button
          @click="openBettingRulesInSportsbook"
        >
          <NuxtIcon
            class="sidebarIcon"
            name="file"
          />
          <span>{{ $t('sidebar.betting_rules') }}</span>
        </Sidebarv2MenuItem>
      </div>
    </div>

    <!-- Precisa de Ajuda? (menu fechado) -->
    <div
      v-if="!isSidebarMenuOpen"
      :class="[
        $style.helpContainerClosed,
        'flex flex-col justify-center items-start self-stretch p-0 w-[70px] rounded-none mx-0 border-0',
      ]"
    >
      <Sidebarv2MenuItem
        v-tooltip.desktopOnly.closedSideBarOnly="$t('sidebar.live_support')"
        is-button
        @click="openSupportChat()"
      >
        <BaseLoading
          v-if="isLoadingOpenSupportChat"
          class="sidebarIcon !bg-transparent !relative"
          :show="true"
          :size="'xsmall'"
        />
        <NuxtIcon
          v-else
          class="sidebarIcon"
          name="menu-lateral/suporte"
        />
      </Sidebarv2MenuItem>

      <Sidebarv2MenuItem
        v-if="finalLinkToBlog"
        v-tooltip.desktopOnly.closedSideBarOnly="$t('sidebar.blog')"
        :draggable="false"
        :target="linkToBlogTargetBlank ? '_blank' : '_self'"
        :to="finalLinkToBlog"
      >
        <NuxtIcon
          class="sidebarIcon"
          name="menu-lateral/blog"
        />
      </Sidebarv2MenuItem>

      <Sidebarv2MenuItem
        v-if="linkToHelpCenter"
        v-tooltip.desktopOnly.closedSideBarOnly="$t('sidebar.help_center')"
        :draggable="false"
        target="_blank"
        :to="linkToHelpCenter"
      >
        <NuxtIcon
          class="sidebarIcon"
          name="menu-lateral/central-ajuda"
        />
      </Sidebarv2MenuItem>

      <Sidebarv2MenuItem
        v-if="isCactusSportsbookActive"
        v-tooltip.desktopOnly.closedSideBarOnly="$t('sidebar.betting_rules')"
        is-button
        @click="openBettingRulesInSportsbook"
      >
        <NuxtIcon
          class="sidebarIcon"
          name="file"
        />
      </Sidebarv2MenuItem>
    </div>

    <!-- Siga-nos (menu aberto) -->
    <div
      v-if="isSidebarMenuOpen"
      class="w-full px-[15px] flex items-center gap-[8px]"
    >
      <span class="text-sidebar-titles font-semibold text-sm"> Siga-nos: </span>
      <template
        v-for="item in socialLinks"
        :key="item.icon"
      >
        <a
          v-if="
            item.url.startsWith('http://') || item.url.startsWith('https://')
          "
          v-tooltip.desktopOnly="item.label"
          :href="item.url"
          rel="noopener"
          target="_blank"
        >
          <NuxtIcon
            class="sidebarIcon text-white text-[18px]"
            :name="item.icon"
          />
        </a>
        <NuxtLink
          v-else
          v-tooltip.desktopOnly="item.label"
          :to="item.url"
        >
          <NuxtIcon
            class="sidebarIcon text-white text-[18px]"
            :name="item.icon"
          />
        </NuxtLink>
      </template>
    </div>

    <!-- Siga-nos (menu fechado) -->
    <template v-if="!isSidebarMenuOpen">
      <div class="flex flex-col items-center max-w-[70px]">
        <template
          v-for="item in socialLinks"
          :key="item.icon"
        >
          <a
            v-if="
              item.url.startsWith('http://') || item.url.startsWith('https://')
            "
            v-tooltip.desktopOnly="item.label"
            class="flex justify-center items-center w-[48px] h-[48px] mb-1.5 mx-auto bg-[#FDF8E9] invert rounded-[6px] border-[1.184px] border-solid border-[rgba(0,0,0,0.1)] cursor-pointer"
            :href="item.url"
            rel="noopener"
            target="_blank"
          >
            <NuxtIcon
              class="sidebarIcon text-[20px] text-black"
              :name="item.icon"
            />
          </a>
          <NuxtLink
            v-else
            v-tooltip.desktopOnly="item.label"
            class="flex justify-center items-center w-[48px] h-[48px] mb-1.5 mx-auto bg-[#FDF8E9] invert rounded-[6px] border-[1.184px] border-solid border-[rgba(0,0,0,0.1)] cursor-pointer"
            :to="item.url"
          >
            <NuxtIcon
              class="sidebarIcon text-[20px] text-black"
              :name="item.icon"
            />
          </NuxtLink>
        </template>
      </div>
    </template>

    <div></div>
  </div>
</template>

<style module lang="scss">
.sidebarContent {
  @apply flex flex-col;
  @apply pt-[10px] gap-[10px] select-none;

  &.isClosed {
    @apply gap-2 pt-0;
  }

  &.darkTheme {
    @apply [background:#000000];
  }

  .tabsContainer {
    @apply border-solid border-sidebar-links/10 px-[15px];
  }

  .tabsWrapper {
    @apply flex h-[35px] items-center shrink-0 self-stretch border [background:#070F22] p-[5px] rounded-[10px] border-solid border-[rgba(255,255,255,0.10)] text-white text-[13px] font-bold leading-[13px] gap-1;

    &.darkTheme {
      @apply [background:#000000];
    }
  }

  .tab {
    @apply flex justify-center items-center gap-2 flex-[1_0_0] self-stretch pt-0 pb-0.5 px-2.5 rounded-md;

    &:hover {
      @apply bg-white/10;
    }

    &.active {
      @apply flex justify-center items-center gap-2.5 flex-[1_0_0] self-stretch [background:#FF450C] pt-0 pb-0.5 px-2.5 rounded-md;
    }
  }

  .helpSection {
    @apply px-[15px] flex flex-col gap-[5px];
  }

  .sectionTitle {
    @apply font-semibold text-[13px];
    color: #939393;
  }

  .helpContainer {
    @apply flex flex-col gap-[4px];
  }

  .helpContainerClosed {
    @apply border-solid border-b border-sidebar-links/10 select-none;
  }

  @media screen and (min-width: 1024px) {
    &.isClosed {
      .tabsContainer {
        @apply hidden;
      }

      .helpSection {
        @apply hidden;
      }
    }
  }
}
</style>
